import { useMemo, useState, type FormEvent } from 'react';
import { color, redcapRuleTheme } from '../styles/theme';
import { dateTime, num } from '../lib/format';
import {
  useCompleteness,
  useDictionary,
  useRedcapSetup,
  useRemoveRedcapProject,
  useSetKeptForms,
  useSetRedcapProject,
  useUploadDictionary,
} from '../api/redcap';
import type { RedcapDictionaryField } from '../api/client';
import { Button, Card, CardTitle, ColHead, Confirm, ErrorNote, Eyebrow, Field, Loading, inputStyle, selectStyle } from '../components/ui';
import { FileButton, GuideCard, MONO, Note, Pill, ProblemNote, Stat, Where, problemOf, type Problem } from './RedcapShared';

/**
 * Project setup — which REDCap project a study's data lives in, and the field
 * list every study's project shares.
 *
 * Every study has its own project (its own PID and records), and every project
 * is built the same (the lab, 5 Oct 2026). So the data dictionary and the forms
 * kept are the lab's, set once here and read by every study (043): the export
 * writes the dictionary's variable names and answer codes, the analytics show
 * codes as their labels, and the record import refuses any column it marks as
 * an identifier and keeps only the forms chosen. Changing them reshapes every
 * study at once, so it takes access to all studies.
 */

const DICTIONARY_MAX_BYTES = 200 * 1024 * 1024;
const EVENT_NAME = /^[a-z][a-z0-9_]{0,99}$/;

function ProjectCard({ slug, canWrite }: { slug: string; canWrite: boolean }) {
  const dict = useDictionary(slug);
  const set = useSetRedcapProject();
  // Unlinking lived on the Weekly completeness tab as "Switch off"; it is the
  // same setting as linking, so it sits beside it. Only while no week is on
  // record — the API answers 409 otherwise, and the button is not offered.
  const completeness = useCompleteness(slug);
  const remove = useRemoveRedcapProject();
  const weeksOnRecord = completeness.data?.history.length ?? 0;
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const project = dict.data?.project ?? null;
  const [editing, setEditing] = useState(false);
  const [pid, setPid] = useState('');
  const [event, setEvent] = useState('');

  const open = () => {
    setPid(project ? String(project.pid) : '');
    setEvent(project?.eventName ?? '');
    setEditing(true);
  };
  const pidOk = /^\d{1,8}$/.test(pid) && Number(pid) > 0;
  const eventOk = event.trim() === '' || EVENT_NAME.test(event.trim());

  function save(e: FormEvent) {
    e.preventDefault();
    if (!pidOk || !eventOk) return;
    set.mutate(
      { slug, pid: Number(pid), eventName: event.trim() === '' ? null : event.trim() },
      { onSuccess: () => setEditing(false) },
    );
  }

  if (dict.isLoading) return <Loading />;
  if (dict.error) return <ErrorNote message={(dict.error as Error).message} />;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Eyebrow>Setup 1 of 3 · this study</Eyebrow>
          <CardTitle style={{ marginBottom: 6 }}>REDCap project</CardTitle>
          <Note>
            The project this study’s records live in. For a longitudinal project, name the unique event a
            website sign-up belongs to — REDCap shows it under Project Setup → Define My Events, e.g.{' '}
            <code style={{ fontFamily: MONO }}>screening_arm_1</code>. Linking it also switches on the weekly
            completeness extract.
          </Note>
        </div>
        {canWrite && !editing && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant={project ? 'ghost' : 'primary'} onClick={open} style={{ padding: '8px 16px', fontSize: 12.5 }}>
              {project ? 'Edit' : 'Link a project'}
            </Button>
            {project && completeness.data && weeksOnRecord === 0 && (
              <Confirm
                open={confirmUnlink}
                title="Unlink this REDCap project?"
                detail="Only possible while no weekly extract is on record. The instrument rules set for this study go too."
                confirmLabel="Unlink"
                align="right"
                onConfirm={() => {
                  setConfirmUnlink(false);
                  remove.mutate(slug);
                }}
                onCancel={() => setConfirmUnlink(false)}
              >
                <Button variant="danger" onClick={() => setConfirmUnlink(true)} style={{ padding: '8px 16px', fontSize: 12.5 }}>
                  Unlink
                </Button>
              </Confirm>
            )}
          </div>
        )}
      </div>

      {project && !editing && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10,
            marginTop: 14,
          }}
        >
          <Stat label="Project ID (PID)" value={num(project.pid)} />
          <Stat label="Event" value={project.eventName ?? 'None — classic project'} />
          <Stat label="Record ID field" value={project.recordIdField ?? 'From the dictionary'} />
          <Stat label="Dictionary" value={project.dictionaryVersion ?? (project.dictionaryUploadedAt ? 'Uploaded' : 'Not yet')} />
        </div>
      )}
      {!project && !editing && (
        <div style={{ marginTop: 12 }}>
          <Note>{canWrite ? 'Not linked yet.' : 'Not linked yet. Someone with the REDCap permission can link it.'}</Note>
        </div>
      )}

      {editing && (
        <form onSubmit={save} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <Field label="Project ID (PID)" style={{ width: 170 }} error={pid && !pidOk ? 'A whole number' : undefined}>
            <input value={pid} onChange={(e) => setPid(e.target.value.trim())} inputMode="numeric" style={inputStyle} />
          </Field>
          <Field
            label="Event (optional)"
            style={{ width: 240 }}
            error={!eventOk ? 'Lowercase letters, numbers and underscores' : undefined}
          >
            <input
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              placeholder="screening_arm_1"
              style={inputStyle}
            />
          </Field>
          <Button type="submit" disabled={!pidOk || !eventOk || set.isPending}>
            {set.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      )}
      {set.error && (
        <div style={{ marginTop: 12 }}>
          <ProblemNote problem={problemOf(set.error)} />
        </div>
      )}
      {remove.error && (
        <div style={{ marginTop: 12 }}>
          <ProblemNote problem={problemOf(remove.error)} />
        </div>
      )}
    </Card>
  );
}

/** Says, once, that the card below is every study's, not this one's. */
function SharedNote({ canWrite }: { canWrite: boolean }) {
  return (
    <div style={{ marginTop: 10 }}>
      <Pill tone={{ bg: color.tint2, fg: color.navy }}>Shared by every study</Pill>
      {!canWrite && (
        <span style={{ fontSize: 12, color: color.muted, marginLeft: 8 }}>
          Changing it takes the REDCap permission and access to all studies.
        </span>
      )}
    </div>
  );
}

function DictionaryCard({ canWrite }: { canWrite: boolean }) {
  const lab = useRedcapSetup();
  const upload = useUploadDictionary();
  const [problem, setProblem] = useState<Problem | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const setup = lab.data?.setup ?? null;
  const summary = lab.data?.summary;

  async function onFile(file: File) {
    setProblem(null);
    setDone(null);
    if (file.size > DICTIONARY_MAX_BYTES) {
      setProblem({ message: 'That file is larger than a data dictionary can be (6 MB).' });
      return;
    }
    try {
      const r = await upload.mutateAsync({ csv: await file.text(), name: file.name });
      setDone(
        `Read ${num(r.summary.fields)} fields across ${num(r.summary.forms.length)} forms` +
          (r.setup.dictionaryVersion ? `, dated ${r.setup.dictionaryVersion}` : '') +
          '. Every study uses it now.',
      );
    } catch (err) {
      setProblem(problemOf(err));
    }
  }

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Eyebrow>Setup 2 of 3 · the lab’s</Eyebrow>
          <CardTitle style={{ marginBottom: 6 }}>Data dictionary</CardTitle>
          <Note>
            In REDCap, open Project Setup → Data Dictionary in any study’s project and press “Download the current
            Data Dictionary”. That is a CSV, and it is the file this takes. The Codebook — the same fields printed
            for people to read, often saved as a PDF — cannot be uploaded. The dictionary is the field list every
            study’s project shares: names, forms and answer codes, never a participant. Upload it again whenever
            the projects change.
          </Note>
          <SharedNote canWrite={canWrite} />
        </div>
        {canWrite && (
          <FileButton
            label={upload.isPending ? 'Reading…' : summary && summary.fields > 0 ? 'Upload a newer one' : 'Upload dictionary'}
            disabled={upload.isPending}
            onFile={onFile}
            variant={summary && summary.fields > 0 ? 'ghost' : 'primary'}
          />
        )}
      </div>

      {lab.isLoading && <Loading />}
      {lab.error && <ErrorNote message={(lab.error as Error).message} />}
      {setup && summary && summary.fields === 0 && (
        <div style={{ marginTop: 12 }}>
          <Note>Not uploaded yet.</Note>
        </div>
      )}
      {setup && summary && summary.fields > 0 && (
        <div style={{ marginTop: 12 }}>
          <Note>
            {num(summary.fields)} fields across {num(summary.forms.length)} forms
            {setup.dictionaryVersion ? `, dated ${setup.dictionaryVersion}` : ''}
            {setup.dictionaryUploadedAt
              ? ` · uploaded ${dateTime(setup.dictionaryUploadedAt)}${setup.dictionaryUploadedByName ? ` by ${setup.dictionaryUploadedByName}` : ''}`
              : ''}
            .
          </Note>
        </div>
      )}
      {done && (
        <div
          role="status"
          style={{
            marginTop: 12,
            background: '#cfe9d8',
            color: '#14563c',
            borderRadius: 14,
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {done}
        </div>
      )}
      {problem && (
        <div style={{ marginTop: 12 }}>
          <ProblemNote problem={problem} />
        </div>
      )}
    </Card>
  );
}

/**
 * Which forms a record import keeps. 07 §7 keeps the trial record in REDCap —
 * CRF scores, labs, randomization — so the choice is the forms recruitment
 * and tracking need; the rest of an export is left out, and the import says
 * which columns and why. A field whose answers name a placebo arm never comes
 * in, whatever form it is on.
 */
function KeptFormsCard({ canWrite }: { canWrite: boolean }) {
  const lab = useRedcapSetup();
  const save = useSetKeptForms();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const setup = lab.data?.setup ?? null;
  const summary = lab.data?.summary;
  if (!setup || !summary || summary.fields === 0) return null;

  const stored = setup.keptForms;
  const current = draft ?? stored ?? [];
  const dirty =
    draft !== null && (stored === null || draft.length !== stored.length || draft.some((f) => !stored.includes(f)));
  const toggle = (form: string) =>
    setDraft(current.includes(form) ? current.filter((f) => f !== form) : [...current, form]);
  const allForms = summary.forms.map((f) => f.name);
  const allTicked = current.length === allForms.length;

  async function onSave() {
    setProblem(null);
    try {
      await save.mutateAsync({ forms: current });
      setDraft(null);
    } catch (err) {
      setProblem(problemOf(err));
    }
  }

  return (
    <Card>
      <Eyebrow>Setup 3 of 3 · the lab’s</Eyebrow>
      <CardTitle style={{ marginBottom: 6 }}>Forms the CRM keeps from an import</CardTitle>
      <Note>
        The trial record stays in REDCap: CRF scores, labs and randomization do not come into the CRM. Tick the
        forms recruitment and tracking need — the intro survey, prescreen, phone screen, consent documentation,
        status. A record import keeps those forms’ columns and leaves the rest out, and says which. Analytics
        count only what was kept.
      </Note>
      <SharedNote canWrite={canWrite} />
      {stored === null && (
        <div style={{ marginTop: 10 }}>
          <Pill tone={redcapRuleTheme.held}>Not chosen yet — no study’s records can be imported</Pill>
        </div>
      )}
      {summary.allocation.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Note>
            Never imported, whatever is ticked — their answers name a placebo arm, so they record who got what:{' '}
            {summary.allocation.map((a, i) => (
              <span key={a.variable}>
                {i > 0 ? ', ' : ''}
                <code style={{ fontFamily: MONO }}>{a.variable}</code> ({a.form})
              </span>
            ))}
            .
          </Note>
        </div>
      )}

      {canWrite && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button
            variant="ghost"
            onClick={() => setDraft(allForms)}
            disabled={save.isPending || allTicked}
            style={{ padding: '6px 14px', fontSize: 12.5 }}
          >
            Select all
          </Button>
          <Button
            variant="ghost"
            onClick={() => setDraft([])}
            disabled={save.isPending || current.length === 0}
            style={{ padding: '6px 14px', fontSize: 12.5 }}
          >
            Clear
          </Button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '4px 14px',
          marginTop: 12,
        }}
      >
        {summary.forms.map((f) => (
          <label
            key={f.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12.5,
              padding: '5px 2px',
              cursor: canWrite ? 'pointer' : 'default',
              minWidth: 0,
            }}
          >
            <input
              type="checkbox"
              checked={current.includes(f.name)}
              disabled={!canWrite || save.isPending}
              onChange={() => toggle(f.name)}
            />
            <code
              style={{
                fontFamily: MONO,
                color: color.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={f.name}
            >
              {f.name}
            </code>
            <span style={{ color: color.muted, flex: 'none' }}>{num(f.fields)}</span>
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
        {canWrite && (
          <>
            <Button onClick={() => void onSave()} disabled={save.isPending || (stored !== null && !dirty)}>
              {save.isPending ? 'Saving…' : stored === null ? 'Save forms kept' : 'Save changes'}
            </Button>
            {dirty && (
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Discard
              </Button>
            )}
          </>
        )}
        <span style={{ fontSize: 12.5, color: color.muted }}>
          {num(current.length)} of {num(summary.forms.length)} forms
          {stored !== null && setup.keptFormsSetAt && !dirty
            ? ` · set ${dateTime(setup.keptFormsSetAt)}${setup.keptFormsSetByName ? ` by ${setup.keptFormsSetByName}` : ''}`
            : ''}
        </span>
      </div>
      {problem && (
        <div style={{ marginTop: 12 }}>
          <ProblemNote problem={problem} />
        </div>
      )}
    </Card>
  );
}

const FIELD_GRID = 'minmax(0,1.1fr) minmax(0,0.6fr) minmax(0,2fr) minmax(0,1.6fr)';
const PAGE = 150;

function FieldBrowser() {
  const dict = useRedcapSetup();
  const forms = dict.data?.summary.forms ?? [];
  const [form, setForm] = useState<string>('');
  const [q, setQ] = useState('');
  const current = form || forms[0]?.name || '';

  const rows = useMemo(() => {
    const all: RedcapDictionaryField[] = dict.data?.fields ?? [];
    const needle = q.trim().toLowerCase();
    return all.filter(
      (f) =>
        (needle ? true : f.form === current) &&
        (!needle || f.variable.includes(needle) || f.label.toLowerCase().includes(needle)),
    );
  }, [dict.data, current, q]);

  if (!dict.data || dict.data.fields.length === 0) return null;

  return (
    <Card pad={0}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '18px 16px 12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <CardTitle style={{ marginBottom: 4 }}>Fields</CardTitle>
          <Note>What every study’s project asks, form by form — the names an export writes and an import reads.</Note>
        </div>
        <Field label="Form" style={{ width: 280, maxWidth: '100%' }}>
          <select value={current} onChange={(e) => setForm(e.target.value)} style={selectStyle} disabled={q.trim() !== ''}>
            {forms.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name} ({f.fields})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Search every form" style={{ width: 220, maxWidth: '100%' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email, age…" style={inputStyle} />
        </Field>
      </div>
      <div className="x-scroll">
        <div style={{ minWidth: 760 }}>
          <div style={{ display: 'grid', gridTemplateColumns: FIELD_GRID, gap: 12, padding: '0 16px 8px' }}>
            <ColHead>Variable</ColHead>
            <ColHead>Type</ColHead>
            <ColHead>Label</ColHead>
            <ColHead>Answers</ColHead>
          </div>
          {rows.slice(0, PAGE).map((f) => (
            <div
              key={f.variable}
              style={{
                display: 'grid',
                gridTemplateColumns: FIELD_GRID,
                gap: 12,
                alignItems: 'start',
                padding: '9px 16px',
                borderTop: `1px solid ${color.line}`,
                fontSize: 12.5,
              }}
            >
              <span style={{ minWidth: 0 }}>
                <code style={{ fontFamily: MONO, color: color.navy, fontWeight: 700, wordBreak: 'break-all' }}>{f.variable}</code>
                {q.trim() !== '' && <span style={{ display: 'block', fontSize: 11, color: color.muted }}>{f.form}</span>}
              </span>
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', color: color.muted }}>
                {f.type}
                {f.validation && <span>· {f.validation}</span>}
                {f.identifier && (
                  <Pill tone={{ bg: '#f6d0c4', fg: '#953a26' }} title="Marked as an identifier in the dictionary">
                    Identifier
                  </Pill>
                )}
              </span>
              <span style={{ color: color.ink, minWidth: 0 }}>{f.label}</span>
              <span style={{ color: color.muted, minWidth: 0 }}>
                {f.choices ? f.choices.map((c) => `${c.code} ${c.label}`).join(' · ') : '—'}
              </span>
            </div>
          ))}
          {rows.length > PAGE && (
            <p style={{ padding: '10px 16px 14px', margin: 0, fontSize: 12.5, color: color.muted }}>
              Showing {PAGE} of {num(rows.length)}. Search to narrow it.
            </p>
          )}
          {rows.length === 0 && (
            <p style={{ padding: '12px 16px 16px', margin: 0, fontSize: 13, color: color.muted }}>No field matches.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * `canWrite` links this study's project; `canWriteSetup` changes what every
 * study shares, which also takes access to all studies.
 */
export function RedcapSetup({
  slug,
  canWrite,
  canWriteSetup,
}: {
  slug: string;
  canWrite: boolean;
  canWriteSetup: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <GuideCard
        storageKey="enact.redcap.guide.setup"
        intro="Three settings, in this order. The first is this study’s; the other two are the lab’s, shared by every study, because every study’s REDCap project is built the same."
        steps={[
          {
            do: 'Link the study’s REDCap project.',
            where: (
              <Where at="REDCap">
                the PID is the number after <code style={{ fontFamily: MONO }}>pid=</code> in the address bar, and at the
                top of Project Setup. For a longitudinal project also copy the unique event name of the sign-up
                event from Project Setup → Define My Events.
              </Where>
            ),
          },
          {
            do: 'Upload the data dictionary — once for the lab.',
            where: (
              <Where at="REDCap">
                Project Setup → Data Dictionary → “Download the current Data Dictionary”, from any study’s project. It
                is a CSV; the Codebook PDF is the same information printed for people and cannot be uploaded. It
                names every form, field and answer code and marks the identifiers; it holds no participant. Upload it
                here again whenever the projects change.
              </Where>
            ),
          },
          {
            do: 'Choose the forms the CRM keeps.',
            where: (
              <Where at="here">
                tick the recruitment and tracking forms — intro survey, prescreen, phone screen, consent
                documentation, status. Rating scales, labs and randomization stay in REDCap. Record imports are
                refused until this is chosen.
              </Where>
            ),
          },
        ]}
      />
      <ProjectCard slug={slug} canWrite={canWrite} />
      <DictionaryCard canWrite={canWriteSetup} />
      <KeptFormsCard canWrite={canWriteSetup} />
      <FieldBrowser />
    </div>
  );
}
